import React, { useState, useRef, useEffect } from "react";
import { useCountry } from "@/lib/country";
import { SUPPORTED_COUNTRIES, SUPPORTED_LANGUAGES, countryMeta } from "@/lib/countries";
import { Globe, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * CountryLanguageSelector
 *
 * Header-mounted dropdown that lets a caregiver set their country (drives
 * /who-pays, /resources, /treatments content) and their app language. The
 * selection is stored on the user record so it sticks across devices.
 *
 * Compact variant: shows the flag + ISO code only (header).
 * Full variant: shows full label + both selects (Settings menu / mobile).
 */
export default function CountryLanguageSelector({ variant = "compact" }) {
    const { country, language, setCountry, setLanguage, t, meta } = useCountry();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const onPickCountry = (code) => {
        if (code === country) {
            setOpen(false);
            return;
        }
        const next = countryMeta(code);
        setCountry(code);
        // When switching country, suggest the default language for that
        // country UNLESS the user has explicitly set a different language
        // already. Heuristic: if the current language is "en" or matches the
        // *previous* country's default, slide to the new country's default.
        if (next.defaultLang && next.defaultLang !== language) {
            setLanguage(next.defaultLang);
        }
        // Close the menu BEFORE firing the toast — sonner uses an extremely
        // high z-index and was intercepting subsequent clicks on top of the
        // open menu, blocking back-to-back country switches.
        setOpen(false);
        toast.dismiss();
        toast.success(t("common.savedToProfile"));
    };

    const onPickLanguage = (code) => {
        if (code === language) {
            setOpen(false);
            return;
        }
        setLanguage(code);
        setOpen(false);
        toast.dismiss();
        toast.success(t("common.savedToProfile"));
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="country-lang-selector-trigger"
                aria-haspopup="true"
                aria-expanded={open}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4C5551] hover:text-teal-dark px-2.5 py-2 rounded-2xl hover:bg-cream-dark transition-colors min-h-[40px]"
                title={t("common.changeCountry")}
            >
                <Globe size={16} strokeWidth={1.7} />
                <span aria-hidden="true" className="text-base leading-none">{meta.flag}</span>
                {variant === "full" ? (
                    <span className="hidden sm:inline">{meta.label}</span>
                ) : (
                    <span className="hidden md:inline text-xs font-semibold">{meta.code}</span>
                )}
                <ChevronDown size={14} strokeWidth={1.8} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    role="menu"
                    data-testid="country-lang-selector-menu"
                    className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-[#E2E8F0] overflow-hidden z-50"
                >
                    <div className="px-4 py-3 bg-cream-light border-b border-[#E2E8F0]">
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-[#4C5551]">{t("common.country")}</p>
                    </div>
                    <ul className="max-h-[440px] overflow-y-auto py-1" data-testid="country-list">
                        {SUPPORTED_COUNTRIES.map((c) => (
                            <li key={c.code}>
                                <button
                                    type="button"
                                    onClick={() => onPickCountry(c.code)}
                                    data-testid={`country-option-${c.code}`}
                                    className={`w-full text-left flex items-center gap-2 px-4 py-2 text-sm hover:bg-cream transition-colors ${country === c.code ? "bg-teal-soft/40 font-medium text-teal-dark" : "text-[#1F2624]"
                                        }`}
                                >
                                    <span className="text-base leading-none" aria-hidden="true">{c.flag}</span>
                                    <span className="flex-1">{c.label}</span>
                                    {country === c.code && <Check size={14} strokeWidth={2.2} className="text-teal-dark" />}
                                </button>
                            </li>
                        ))}
                    </ul>
                    <div className="px-4 py-3 bg-cream-light border-y border-[#E2E8F0]">
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-[#4C5551]">{t("common.language")}</p>
                    </div>
                    <ul className="py-1" data-testid="language-list">
                        {SUPPORTED_LANGUAGES.map((l) => (
                            <li key={l.code}>
                                <button
                                    type="button"
                                    onClick={() => onPickLanguage(l.code)}
                                    data-testid={`language-option-${l.code}`}
                                    className={`w-full text-left flex items-center gap-2 px-4 py-2 text-sm hover:bg-cream transition-colors ${language === l.code ? "bg-teal-soft/40 font-medium text-teal-dark" : "text-[#1F2624]"
                                        }`}
                                >
                                    <span className="flex-1">{l.label}</span>
                                    {language === l.code && <Check size={14} strokeWidth={2.2} className="text-teal-dark" />}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
